import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import IosShareIcon from "@mui/icons-material/IosShare";
import LinkIcon from "@mui/icons-material/Link";
import { PageHeader } from "@/components/AppShell";
import {
  pharmacyAdminBookingsApi,
  type BookingPharmacy,
  type Room,
  type Booking,
  type Availability,
} from "@/lib/callables";
import { useToast } from "@/lib/toast";

const BOOKING_BASE =
  import.meta.env.VITE_BOOKING_BASE_URL ?? "https://ccs-booking-dev.web.app";

export default function BookingsPage() {
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string | null>(null);

  const pharmaciesQ = useQuery({
    queryKey: ["bookings", "pharmacies"],
    queryFn: () => pharmacyAdminBookingsApi.listBookingPharmacies(),
  });

  const pharmacies = pharmaciesQ.data?.items ?? [];
  const hasAmbiguity = pharmacies.length > 1;

  // The callable returns records under the shared canonical rule, canonical
  // first (isPrimary). Duplicates are leftovers of the SAME pharmacy, not
  // distinct locations, so the canonical record is always the correct
  // default — it is the one the pharmacist's app writes availability to and
  // the one the patient booking link resolves to. Refusing to default (the
  // previous behaviour) left admins staring at an empty page, and letting
  // them pick a duplicate handed out a link with no availability behind it.
  const selectedPharmacy =
    pharmacies.find((p) => p.id === selectedPharmacyId) ?? pharmacies[0] ?? null;

  useEffect(() => {
    if (pharmacies.length === 0) return;
    // Re-evaluate whenever the underlying pharmacies data changes (not just
    // on first load): default to the canonical record when nothing is
    // selected yet, and repoint away from a selection that no longer exists
    // in a refreshed list (e.g. a duplicate got merged/removed).
    const stillExists = pharmacies.some((p) => p.id === selectedPharmacyId);
    if (!selectedPharmacyId || !stillExists) {
      setSelectedPharmacyId(pharmacies[0]!.id);
    }
  }, [pharmacies, selectedPharmacyId]);

  if (pharmaciesQ.isLoading) {
    return <Box sx={{ p: 4, textAlign: "center" }}>Loading...</Box>;
  }

  if (pharmacies.length === 0) {
    return (
      <>
        <PageHeader title="Bookings" />
        <Box sx={{ p: 4 }}>
          <Alert severity="info">
            No booking setup yet for this pharmacy. Ask a pharmacist to open the CCS Pharmacy app and complete the Bookings setup from their device — it will appear here automatically.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Bookings"
        description="Manage appointments."
        actions={
          hasAmbiguity && (
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Pharmacy Location</InputLabel>
              <Select
                value={selectedPharmacyId || ""}
                label="Pharmacy Location"
                displayEmpty
                onChange={(e) => setSelectedPharmacyId(e.target.value)}
              >
                {pharmacies.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                    {p.isPrimary ? " · Primary" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }
      />
      {hasAmbiguity && (
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 2 }}>
          <Alert severity="warning">
            More than one booking record exists for this pharmacy. The record marked
            "Primary" is showing — it is the one the patient booking link and the mobile
            app's availability writes both resolve to. The others are leftover
            duplicates and should be merged by support (booking-record reconciliation);
            a link generated from one of them will show no availability.
          </Alert>
        </Box>
      )}
      {selectedPharmacy && (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <BookingsTab
            bookingPharmacyId={selectedPharmacy.id}
            // The shareable link ALWAYS points at the canonical record
            // (pharmacies[0]), even if the admin is inspecting a
            // leftover duplicate above — a link built from a duplicate
            // resolves to an empty availability subcollection.
            linkPharmacyId={pharmacies[0]!.id}
          />
        </Box>
      )}
    </>
  );
}

// ── Bookings tab ────────────────────────────────────────────────────────

function BookingLinkCard({ bookingPharmacyId }: { bookingPharmacyId: string }) {
  const { showSuccess } = useToast();
  const url = `${BOOKING_BASE}?pharmacyId=${bookingPharmacyId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showSuccess("Booking link copied to clipboard");
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      showSuccess("Booking link copied to clipboard");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Book a Consultation",
          text: "Use this link to book a pharmacy consultation online.",
          url,
        });
      } catch {
        // user cancelled or share failed — do nothing
      }
    } else {
      await handleCopy();
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
        <LinkIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Patient Booking Link
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Share this link with patients so they can book a consultation at this pharmacy.
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          bgcolor: "action.hover",
          borderRadius: 1,
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{ flexGrow: 1, fontFamily: "monospace", wordBreak: "break-all" }}
        >
          {url}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopy}
          >
            Copy
          </Button>
          {typeof navigator !== "undefined" && "share" in navigator && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<IosShareIcon fontSize="small" />}
              onClick={handleShare}
            >
              Share
            </Button>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

function BookingsTab({
  bookingPharmacyId,
  linkPharmacyId,
}: {
  bookingPharmacyId: string;
  linkPharmacyId: string;
}) {
  const { showError, showSuccess } = useToast();
  const [filters, setFilters] = useState({
    status: "all" as string,
    pharmacistId: "all",
    startDate: "",
    endDate: "",
  });
  const [reschedulingBooking, setReschedulingBooking] = useState<Booking | null>(null);

  const roomsQ = useQuery({
    queryKey: ["bookings", "rooms", bookingPharmacyId],
    queryFn: () => pharmacyAdminBookingsApi.listRooms({ bookingPharmacyId }),
  });

  const pharmacistsQ = useQuery({
    queryKey: ["bookings", "availability-pharmacists", bookingPharmacyId],
    queryFn: () => pharmacyAdminBookingsApi.listAvailabilityPharmacists({ bookingPharmacyId }),
  });

  const bookingsQ = useQuery({
    queryKey: ["bookings", "list", bookingPharmacyId, filters],
    queryFn: () =>
      pharmacyAdminBookingsApi.listBookings({
        bookingPharmacyId,
        status: filters.status === "all" ? undefined : (filters.status as any),
        pharmacistId: filters.pharmacistId === "all" ? undefined : filters.pharmacistId,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }),
  });

  const updateStatusM = useMutation({
    mutationFn: (data: Parameters<typeof pharmacyAdminBookingsApi.updateBookingStatus>[0]) =>
      pharmacyAdminBookingsApi.updateBookingStatus(data),
    onSuccess: () => {
      showSuccess("Booking status updated");
      bookingsQ.refetch();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed"),
  });

  const exportCsv = () => {
    const data = bookingsQ.data?.items || [];
    if (data.length === 0) return;

    const rooms = roomsQ.data?.items || [];
    const pharmacists = pharmacistsQ.data?.items || [];
    const headers = ["ID", "Date", "Start", "End", "Room", "Pharmacist", "Status", "Patient Email", "Notes"];
    const rows = data.map((b) => [
      b.id,
      b.date,
      b.startTime,
      b.endTime,
      rooms.find((r) => r.id === b.roomId)?.name || b.roomId,
      pharmacists.find((p) => p.pharmacistId === b.pharmacistId)?.displayName || b.pharmacistId || "",
      b.status,
      b.patientEmail,
      `"${b.notes.replace(/"/g, '""')}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `bookings_${bookingPharmacyId}_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const rooms = roomsQ.data?.items || [];
  const pharmacists = pharmacistsQ.data?.items || [];
  const bookings = bookingsQ.data?.items || [];

  return (
    <Stack spacing={3}>
      <BookingLinkCard bookingPharmacyId={linkPharmacyId} />

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="rescheduled">Rescheduled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="no_show">No Show</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Pharmacist</InputLabel>
            <Select
              value={filters.pharmacistId}
              label="Pharmacist"
              onChange={(e) => setFilters((f) => ({ ...f, pharmacistId: e.target.value }))}
            >
              <MenuItem value="all">All Pharmacists</MenuItem>
              {pharmacists.map((p) => (
                <MenuItem key={p.pharmacistId} value={p.pharmacistId}>
                  {p.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="From"
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={filters.startDate}
            onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={filters.endDate}
            onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
          />

          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<FileDownloadIcon />}
            onClick={exportCsv}
            disabled={bookings.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Room</TableCell>
              <TableCell>Pharmacist</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookingsQ.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No bookings found.
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {b.date}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {b.startTime} - {b.endTime}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {rooms.find((r) => r.id === b.roomId)?.name || b.roomId}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {pharmacists.find((p) => p.pharmacistId === b.pharmacistId)?.displayName ||
                        (b.pharmacistId ? b.pharmacistId.slice(0, 8) + "…" : "—")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{b.patientEmail}</Typography>
                    {b.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {b.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={b.status}
                      size="small"
                      color={
                        b.status === "confirmed"
                          ? "success"
                          : b.status === "pending"
                          ? "warning"
                          : b.status === "completed"
                          ? "info"
                          : "default"
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    {(b.status === "pending" || b.status === "confirmed") && (
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          onClick={() =>
                            updateStatusM.mutate({
                              bookingPharmacyId,
                              bookingId: b.id,
                              status: "confirmed",
                            })
                          }
                          disabled={b.status === "confirmed"}
                        >
                          Confirm
                        </Button>
                        <Button size="small" onClick={() => setReschedulingBooking(b)}>
                          Reschedule
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            updateStatusM.mutate({
                              bookingPharmacyId,
                              bookingId: b.id,
                              status: "cancelled",
                            })
                          }
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          onClick={() =>
                            updateStatusM.mutate({
                              bookingPharmacyId,
                              bookingId: b.id,
                              status: "completed",
                            })
                          }
                        >
                          Complete
                        </Button>
                        <Button
                          size="small"
                          onClick={() =>
                            updateStatusM.mutate({
                              bookingPharmacyId,
                              bookingId: b.id,
                              status: "no_show",
                            })
                          }
                        >
                          No-show
                        </Button>
                      </Stack>
                    )}
                    {b.status === "cancelled" && (
                      <Button
                        size="small"
                        onClick={() =>
                          updateStatusM.mutate({
                            bookingPharmacyId,
                            bookingId: b.id,
                            status: "confirmed",
                          })
                        }
                      >
                        Re-confirm
                      </Button>
                    )}
                    {b.status === "completed" && (
                      <Button
                        size="small"
                        onClick={() =>
                          updateStatusM.mutate({
                            bookingPharmacyId,
                            bookingId: b.id,
                            status: "no_show",
                          })
                        }
                      >
                        No-show
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {reschedulingBooking && (
        <RescheduleDialog
          open={!!reschedulingBooking}
          booking={reschedulingBooking}
          bookingPharmacyId={bookingPharmacyId}
          onClose={() => setReschedulingBooking(null)}
          onSuccess={() => {
            setReschedulingBooking(null);
            bookingsQ.refetch();
          }}
        />
      )}
    </Stack>
  );
}

// ── Reschedule dialog ────────────────────────────────────────────────────

function RescheduleDialog({
  open,
  booking,
  bookingPharmacyId,
  onClose,
  onSuccess,
}: {
  open: boolean;
  booking: Booking;
  bookingPharmacyId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showError, showSuccess } = useToast();
  const [date, setDate] = useState(booking.date);
  const [startTime, setStartTime] = useState(booking.startTime);
  const [endTime, setEndTime] = useState(booking.endTime);

  const rescheduleM = useMutation({
    mutationFn: (data: Parameters<typeof pharmacyAdminBookingsApi.rescheduleBooking>[0]) =>
      pharmacyAdminBookingsApi.rescheduleBooking(data),
    onSuccess: () => {
      showSuccess("Booking rescheduled");
      onSuccess();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed to reschedule"),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Reschedule Booking</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="New Date"
            type="date"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <TextField
            label="Start Time"
            type="time"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
          <TextField
            label="End Time"
            type="time"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() =>
            rescheduleM.mutate({
              bookingPharmacyId,
              bookingId: booking.id,
              newDate: date,
              newStartTime: startTime,
              newEndTime: endTime,
            })
          }
          disabled={rescheduleM.isPending}
        >
          {rescheduleM.isPending ? "Rescheduling..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Suppress unused import warnings — these types are exported for reference
export type { BookingPharmacy, Room, Booking, Availability };
